import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, TextField, Dialog, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert, CircularProgress, Tabs, Tab, IconButton, Tooltip, LinearProgress, List, ListItem, ListItemText, ListItemIcon, FormControl, InputLabel, Select, MenuItem, Accordion, AccordionSummary, AccordionDetails, Divider, CardActions, DialogTitle, DialogContent, DialogActions, Switch, FormControlLabel
} from '@mui/material';

import {
  BeachAccess as BeachAccessIcon, Warning as WarningIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Info as InfoIcon, ExpandMore as ExpandMoreIcon, CalendarToday as CalendarIcon, People as PeopleIcon, Assessment as AssessmentIcon, Refresh as RefreshIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Notifications as NotificationsIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, Schedule as ScheduleIcon, History as HistoryIcon, Analytics as AnalyticsIcon
} from '@mui/icons-material';

import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/api';
import { formatCalendarDate } from '../utils/dates';

interface Customer {
  id: number;
  name: string;
  phone_number: string;
  email: string;
}

interface CaregiverCircle {
  id: number;
  customer_id: number;
  caregiver_name: string;
  caregiver_email: string | null;
  caregiver_phone: string | null;
  role: string;
  is_primary: boolean;
  is_active: boolean;
}

interface VacationPeriod {
  id: number;
  schedule_id: number;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  auto_resume: boolean;
  notify_caregiver: boolean;
  created_at: string;
  updated_at: string | null;
}

interface VacationConflict {
  conflict_level: string;
  affected_caregivers: number[];
  available_backup_caregivers: number[];
  conflict_period: {
    start_date: string;
    end_date: string;
  };
  resolution_suggestions: string[];
}

interface VacationSchedule {
  customer_id: number;
  period: {
    start_date: string;
    end_date: string;
  };
  caregivers: Array<{
    caregiver_id: number;
    caregiver_name: string;
    role: string;
    is_primary: boolean;
    vacations: Array<{
      id: number;
      name: string;
      start_date: string;
      end_date: string;
      duration_days: number;
      is_active: boolean;
    }>;
    total_vacation_days: number;
  }>;
  customer_vacations?: Array<{
    id: number;
    name: string;
    description?: string | null;
    start_date: string;
    end_date: string;
    duration_days: number;
    is_active: boolean;
  }>;
  conflicts: VacationConflict[];
  summary: {
    total_caregivers: number;
    available_caregivers: number;
    vacation_caregivers: number;
    conflict_days: number;
  };
}

interface BackupSuggestion {
  caregiver_id: number;
  caregiver_name: string;
  role: string;
  is_primary: boolean;
  availability_score: number;
  recommendation_reason: string;
}

interface VacationStats {
  customer_id: number;
  period: {
    start_date: string;
    end_date: string;
  };
  caregiver_stats: {
    total_caregivers: number;
    available_caregivers: number;
    vacation_caregivers: number;
    availability_percentage: number;
  };
  conflict_stats: {
    total_conflicts: number;
    conflicts_by_level: Record<string, number>;
  };
  vacation_stats: {
    total_vacation_days: number;
    average_vacation_duration: number;
    longest_vacation: number;
    shortest_vacation: number;
  };
}

const EnhancedVacationDashboard: React.FC = () => {
  const { token } = useAuth();
  const apiBase = getApiUrl();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
  const [caregiverCircles, setCaregiverCircles] = useState<CaregiverCircle[]>([]);
  const [vacationSchedule, setVacationSchedule] = useState<VacationSchedule | null>(null);
  const [vacationStats, setVacationStats] = useState<VacationStats | null>(null);
  const [backupSuggestions, setBackupSuggestions] = useState<BackupSuggestion[]>([]);
  const [conflictLevels, setConflictLevels] = useState<any[]>([]);
  
  // Dialog states
  const [caregiverVacationDialog, setCaregiverVacationDialog] = useState(false);
  const [conflictCheckDialog, setConflictCheckDialog] = useState(false);
  const [backupSuggestionsDialog, setBackupSuggestionsDialog] = useState(false);
  const [statsDialog, setStatsDialog] = useState(false);
  
  // Form states
  const [caregiverVacationForm, setCaregiverVacationForm] = useState({
    caregiver_circle_id: '',
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    vacation_type: 'customer', // 'customer' or 'caregiver'
    is_active: true,
    auto_resume: true,
    notify_caregiver: true
  });
  
  const [conflictCheckForm, setConflictCheckForm] = useState({
    start_date: '',
    end_date: ''
  });
  
  const [backupSuggestionsForm, setBackupSuggestionsForm] = useState({
    start_date: '',
    end_date: ''
  });

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  useEffect(() => {
    loadCustomers();
    loadConflictLevels();
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      loadCaregiverCircles();
      loadVacationSchedule();
      loadVacationStats();
    }
  }, [selectedCustomer]);

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${apiBase}/customers/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
      setError('Failed to load customers');
    }
  };

  const loadCaregiverCircles = async () => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${apiBase}/caregiver-circles/customers/${selectedCustomer}/caregivers/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setCaregiverCircles(data.caregiver_circles || []);
    } catch (error) {
      console.error('Error loading caregiver circles:', error);
      setError('Failed to load caregiver circles');
    }
  };

  const loadVacationSchedule = async () => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${apiBase}/enhanced-vacation/schedule/${selectedCustomer}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setVacationSchedule(data.data);
    } catch (error) {
      console.error('Error loading vacation schedule:', error);
      setError('Failed to load vacation schedule');
    }
  };

  const loadVacationStats = async () => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${apiBase}/enhanced-vacation/vacation-stats/${selectedCustomer}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setVacationStats(data.data);
    } catch (error) {
      console.error('Error loading vacation stats:', error);
      setError('Failed to load vacation stats');
    }
  };

  const loadConflictLevels = async () => {
    try {
      const response = await fetch(`${apiBase}/enhanced-vacation/conflict-levels`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setConflictLevels(data.data.conflict_levels);
    } catch (error) {
      console.error('Error loading conflict levels:', error);
    }
  };

  const createVacation = async () => {
    if (!selectedCustomer) {
      setError('Please select a loved one first');
      return;
    }
    if (!caregiverVacationForm.name.trim()) {
      setError('Vacation name is required');
      return;
    }
    if (!caregiverVacationForm.start_date || !caregiverVacationForm.end_date) {
      setError('Start date and end date are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const isLovedOneVacation = caregiverVacationForm.vacation_type === 'customer';

      if (isLovedOneVacation) {
        // Loved one vacation — pauses check-ins while they are away
        const response = await fetch(
          `${apiBase}/enhanced-vacation/customer-vacation/${selectedCustomer}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              name: caregiverVacationForm.name,
              description: caregiverVacationForm.description,
              start_date: caregiverVacationForm.start_date,
              end_date: caregiverVacationForm.end_date,
              is_active: caregiverVacationForm.is_active,
              auto_resume: caregiverVacationForm.auto_resume,
              notify_caregiver: caregiverVacationForm.notify_caregiver,
            }),
          }
        );

        if (!response.ok) {
          let message = 'Failed to create loved one vacation';
          try {
            const err = await response.json();
            message = typeof err.detail === 'string' ? err.detail : message;
          } catch {
            // keep fallback
          }
          throw new Error(message);
        }
      } else {
        if (!caregiverVacationForm.caregiver_circle_id) {
          setError('Please select a caregiver for caregiver vacation');
          setLoading(false);
          return;
        }

        const response = await fetch(`${apiBase}/enhanced-vacation/caregiver-vacation`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            caregiver_circle_id: parseInt(caregiverVacationForm.caregiver_circle_id, 10),
            name: caregiverVacationForm.name,
            description: caregiverVacationForm.description,
            start_date: caregiverVacationForm.start_date,
            end_date: caregiverVacationForm.end_date,
            is_active: caregiverVacationForm.is_active,
            auto_resume: caregiverVacationForm.auto_resume,
            notify_caregiver: caregiverVacationForm.notify_caregiver,
            vacation_type: 'caregiver',
          }),
        });

        if (!response.ok) {
          let message = 'Failed to create caregiver vacation';
          try {
            const err = await response.json();
            message = typeof err.detail === 'string' ? err.detail : message;
          } catch {
            // keep fallback
          }
          throw new Error(message);
        }
      }

      setCaregiverVacationDialog(false);
      setCaregiverVacationForm({
        caregiver_circle_id: '',
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        vacation_type: 'customer',
        is_active: true,
        auto_resume: true,
        notify_caregiver: true,
      });

      await Promise.all([loadVacationSchedule(), loadVacationStats()]);
    } catch (error) {
      console.error('Error creating vacation:', error);
      setError(error instanceof Error ? error.message : 'Failed to create vacation');
    } finally {
      setLoading(false);
    }
  };

  const checkConflicts = async () => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${apiBase}/enhanced-vacation/conflicts/${selectedCustomer}?start_date=${conflictCheckForm.start_date}&end_date=${conflictCheckForm.end_date}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // Handle conflicts data
      console.log('Conflicts:', data.data);
      
      setConflictCheckDialog(false);
      setConflictCheckForm({
        start_date: '',
        end_date: ''
      });
    } catch (error) {
      console.error('Error checking conflicts:', error);
      setError('Failed to check conflicts');
    }
  };

  const getBackupSuggestions = async () => {
    if (!selectedCustomer) return;
    
    try {
      const response = await fetch(`${apiBase}/enhanced-vacation/backup-suggestions/${selectedCustomer}?start_date=${backupSuggestionsForm.start_date}&end_date=${backupSuggestionsForm.end_date}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setBackupSuggestions(data.data.suggestions);
      setBackupSuggestionsDialog(true);
    } catch (error) {
      console.error('Error getting backup suggestions:', error);
      setError('Failed to get backup suggestions');
    }
  };

  const getConflictLevelColor = (level: string) => {
    const conflictLevel = conflictLevels.find(cl => cl.level === level);
    return conflictLevel?.color || '#666';
  };

  const getConflictLevelSeverity = (level: string) => {
    switch (level) {
      case 'none': return 'success';
      case 'low': return 'warning';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const renderOverviewTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Enhanced Vacation Overview</Typography>
      
      {selectedCustomer && vacationStats && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Caregiver Availability</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ flex: 1, mr: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={vacationStats.caregiver_stats.availability_percentage}
                      color={vacationStats.caregiver_stats.availability_percentage > 70 ? 'success' : 'warning'}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="h6">{vacationStats.caregiver_stats.availability_percentage}%</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {vacationStats.caregiver_stats.available_caregivers} of {vacationStats.caregiver_stats.total_caregivers} caregivers available
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Conflict Status</Typography>
                <Typography variant="h4" color={vacationStats.conflict_stats.total_conflicts > 0 ? 'error' : 'success'}>
                  {vacationStats.conflict_stats.total_conflicts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active conflicts detected
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Vacation Statistics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Total Vacation Days</Typography>
                    <Typography variant="h6">{vacationStats.vacation_stats.total_vacation_days}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Average Duration</Typography>
                    <Typography variant="h6">{vacationStats.vacation_stats.average_vacation_duration} days</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Longest Vacation</Typography>
                    <Typography variant="h6">{vacationStats.vacation_stats.longest_vacation} days</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">Shortest Vacation</Typography>
                    <Typography variant="h6">{vacationStats.vacation_stats.shortest_vacation} days</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  const renderScheduleTab = () => (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Vacation Schedule</Typography>
        <Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCaregiverVacationDialog(true)}
            sx={{ mr: 1 }}
          >
            Add Vacation
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => setStatsDialog(true)}
          >
            View Statistics
          </Button>
        </Box>
      </Box>

      {vacationSchedule && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Loved One Vacations</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  While the loved one is on vacation, check-ins are paused.
                </Typography>
                {(vacationSchedule.customer_vacations || []).length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Dates</TableCell>
                          <TableCell>Days</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(vacationSchedule.customer_vacations || []).map((vacation) => (
                          <TableRow key={vacation.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">{vacation.name}</Typography>
                              {vacation.description && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {vacation.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {formatCalendarDate(vacation.start_date)} –{' '}
                              {formatCalendarDate(vacation.end_date)}
                            </TableCell>
                            <TableCell>{vacation.duration_days}</TableCell>
                            <TableCell>
                              {vacation.is_active ? (
                                <Chip label="Active / Check-ins paused" size="small" color="warning" />
                              ) : (
                                <Chip label="Inactive" size="small" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No loved one vacations scheduled. Use Add Vacation and choose Loved One Vacation.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Caregiver Vacation Schedule</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Caregiver</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Vacations</TableCell>
                        <TableCell>Total Days</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vacationSchedule.caregivers.map((caregiver) => (
                        <TableRow key={caregiver.caregiver_id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {caregiver.caregiver_name}
                              </Typography>
                              {caregiver.is_primary && (
                                <Chip label="Primary" size="small" color="primary" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={caregiver.role} 
                              size="small" 
                              color={caregiver.role === 'primary' ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            {caregiver.vacations.length > 0 ? (
                              <List dense>
                                {caregiver.vacations.map((vacation) => (
                                  <ListItem key={vacation.id} sx={{ py: 0 }}>
                                    <ListItemText
                                      primary={vacation.name}
                                      secondary={`${formatCalendarDate(vacation.start_date)} - ${formatCalendarDate(vacation.end_date)}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No vacations
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {caregiver.total_vacation_days} days
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {caregiver.vacations.some(v => v.is_active) ? (
                              <Chip label="On Vacation" size="small" color="warning" />
                            ) : (
                              <Chip label="Available" size="small" color="success" />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" color="error">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {vacationSchedule.conflicts.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="error">
                    Vacation Conflicts Detected
                  </Typography>
                  <List>
                    {vacationSchedule.conflicts.map((conflict, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <WarningIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${conflict.conflict_level.toUpperCase()} Conflict`}
                          secondary={
                            <Box>
                              <Typography variant="body2">
                                Period: {formatCalendarDate(conflict.conflict_period.start_date)} - {formatCalendarDate(conflict.conflict_period.end_date)}
                              </Typography>
                              <Typography variant="body2">
                                Affected Caregivers: {conflict.affected_caregivers.length}
                              </Typography>
                              <Typography variant="body2">
                                Available Backup: {conflict.available_backup_caregivers.length}
                              </Typography>
                              <List dense>
                                {conflict.resolution_suggestions.map((suggestion, idx) => (
                                  <ListItem key={idx} sx={{ py: 0 }}>
                                    <ListItemText primary={suggestion} />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );

  const renderConflictsTab = () => (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Conflict Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<AssessmentIcon />}
            onClick={() => setConflictCheckDialog(true)}
            sx={{ mr: 1 }}
          >
            Check Conflicts
          </Button>
          <Button
            variant="outlined"
            startIcon={<PeopleIcon />}
            onClick={() => setBackupSuggestionsDialog(true)}
          >
            Get Backup Suggestions
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Conflict Levels</Typography>
              <List>
                {conflictLevels.map((level) => (
                  <ListItem key={level.level}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          backgroundColor: level.color
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={level.level.toUpperCase()}
                      secondary={level.description}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Backup Suggestions</Typography>
              {backupSuggestions.length > 0 ? (
                <List>
                  {backupSuggestions.slice(0, 5).map((suggestion) => (
                    <ListItem key={suggestion.caregiver_id}>
                      <ListItemText
                        primary={suggestion.caregiver_name}
                        secondary={
                          <Box>
                            <Typography variant="body2">
                              Role: {suggestion.role} {suggestion.is_primary && '(Primary)'}
                            </Typography>
                            <Typography variant="body2">
                              Score: {suggestion.availability_score}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {suggestion.recommendation_reason}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No backup suggestions available
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderHistoryTab = () => (
    <Box>
      <Typography variant="h6" gutterBottom>Vacation History</Typography>
      <Typography variant="body2" color="text.secondary">
        Vacation history tracking and analytics will be displayed here.
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Enhanced Vacation Mode
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Customer</InputLabel>
          <Select
            value={selectedCustomer || ''}
            onChange={(e) => setSelectedCustomer(e.target.value as number)}
            label="Select Customer"
          >
            {customers.map((customer) => (
              <MenuItem key={customer.id} value={customer.id}>
                {customer.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedCustomer && (
        <Box>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Overview" icon={<AssessmentIcon />} />
            <Tab label="Schedule" icon={<ScheduleIcon />} />
            <Tab label="Conflicts" icon={<WarningIcon />} />
            <Tab label="History" icon={<HistoryIcon />} />
          </Tabs>

          {activeTab === 0 && renderOverviewTab()}
          {activeTab === 1 && renderScheduleTab()}
          {activeTab === 2 && renderConflictsTab()}
          {activeTab === 3 && renderHistoryTab()}
        </Box>
      )}

      {/* Vacation Dialog — loved one or caregiver */}
      <Dialog open={caregiverVacationDialog} onClose={() => setCaregiverVacationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Vacation Period</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            Loved one vacation pauses check-ins through the end date (inclusive)—check-ins resume the next day.
            Caregiver vacation only reroutes alerts to other circle members.
          </Alert>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Vacation Type</InputLabel>
                <Select
                  value={caregiverVacationForm.vacation_type}
                  onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, vacation_type: e.target.value})}
                  label="Vacation Type"
                >
                  <MenuItem value="customer">Loved One Vacation (pauses check-ins while they are away)</MenuItem>
                  <MenuItem value="caregiver">Caregiver Vacation (alerts go to next available caregiver)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {caregiverVacationForm.vacation_type === 'caregiver' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Caregiver</InputLabel>
                  <Select
                    value={caregiverVacationForm.caregiver_circle_id}
                    onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, caregiver_circle_id: e.target.value})}
                    label="Caregiver"
                  >
                    {caregiverCircles.map((caregiver) => (
                      <MenuItem key={caregiver.id} value={caregiver.id}>
                        {caregiver.caregiver_name} ({caregiver.role})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vacation Name"
                value={caregiverVacationForm.name}
                onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, name: e.target.value})}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={caregiverVacationForm.description}
                onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, description: e.target.value})}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={caregiverVacationForm.start_date}
                onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, start_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date (last day paused)"
                type="date"
                value={caregiverVacationForm.end_date}
                onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, end_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                helperText="Check-ins resume the day after this date"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={caregiverVacationForm.is_active}
                    onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, is_active: e.target.checked})}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={caregiverVacationForm.auto_resume}
                    onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, auto_resume: e.target.checked})}
                  />
                }
                label="Auto Resume"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={caregiverVacationForm.notify_caregiver}
                    onChange={(e) => setCaregiverVacationForm({...caregiverVacationForm, notify_caregiver: e.target.checked})}
                  />
                }
                label="Notify Care Circle"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCaregiverVacationDialog(false)}>Cancel</Button>
          <Button onClick={createVacation} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Create Vacation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Conflict Check Dialog */}
      <Dialog open={conflictCheckDialog} onClose={() => setConflictCheckDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Check Vacation Conflicts</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={conflictCheckForm.start_date}
                onChange={(e) => setConflictCheckForm({...conflictCheckForm, start_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={conflictCheckForm.end_date}
                onChange={(e) => setConflictCheckForm({...conflictCheckForm, end_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictCheckDialog(false)}>Cancel</Button>
          <Button onClick={checkConflicts} variant="contained">Check Conflicts</Button>
        </DialogActions>
      </Dialog>

      {/* Backup Suggestions Dialog */}
      <Dialog open={backupSuggestionsDialog} onClose={() => setBackupSuggestionsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Backup Caregiver Suggestions</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={backupSuggestionsForm.start_date}
                onChange={(e) => setBackupSuggestionsForm({...backupSuggestionsForm, start_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={backupSuggestionsForm.end_date}
                onChange={(e) => setBackupSuggestionsForm({...backupSuggestionsForm, end_date: e.target.value})}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          {backupSuggestions.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>Available Caregivers</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Caregiver</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Availability Score</TableCell>
                      <TableCell>Recommendation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backupSuggestions.map((suggestion) => (
                      <TableRow key={suggestion.caregiver_id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {suggestion.caregiver_name}
                            </Typography>
                            {suggestion.is_primary && (
                              <Chip label="Primary" size="small" color="primary" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={suggestion.role} 
                            size="small" 
                            color={suggestion.role === 'primary' ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={(suggestion.availability_score / 20) * 100}
                                color="success"
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Typography variant="body2">
                              {suggestion.availability_score}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {suggestion.recommendation_reason}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupSuggestionsDialog(false)}>Close</Button>
          <Button onClick={getBackupSuggestions} variant="contained">Get Suggestions</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedVacationDashboard;
